/**
 * Shared optional co-borrower answer model (WI-TV-W7-001).
 *
 * Both journeys ask the same optional co-borrower questions, so they share one
 * namespaced model rather than two purpose-specific ones. This module owns that
 * model and nothing else: the value keys, the single sanitizer, the single
 * clear, and the translation of the published nested `coBorrower` prefill object
 * into those keys. It renders nothing, listens for nothing, and decides no
 * trust — values reach it only after `handlePrefillMessage` has accepted the
 * sender's exact allowlisted origin.
 *
 * Three properties of the model are deliberate:
 *
 * - **Namespaced.** Every key is prefixed `coBorrower…` and none of them is a
 *   primary-borrower key, so a secondary answer can never be read, submitted, or
 *   cleared as if it were the applicant's own. The model is intentionally
 *   smaller than the primary one: there is no co-borrower credit, military,
 *   employment, or income answer, and `coBorrower.creditScoreRange` is ignored
 *   on the prefill path rather than stored under a co-borrower key.
 * - **Opt-in.** The secondary borrower exists only when the borrower (or an
 *   accepted trusted prefill) says so. {@link COBORROWER_OPT_IN_KEY} carries
 *   that statement, and accepting any supported co-borrower answer sets it, so
 *   there is no co-borrower state that is not also visibly opted in.
 * - **Completely clearable.** {@link clearCoBorrowerValues} is the one clear
 *   operation. It removes every co-borrower answer *and* the opt-in together and
 *   is idempotent, so No / Skip / Remove and the destructive loan-purpose change
 *   all leave a snapshot with no secondary value in it, and re-adding a
 *   co-borrower later starts from nothing rather than resurrecting stale values.
 *
 * Values are only ever seeded: nothing here advances a step, submits, or is
 * logged, and this WI adds no visible co-borrower step and no submission-payload
 * projection for these keys.
 */
import { isValidStateCode } from "../form/validation";
import { isValidEmailInput, isValidPhoneInput, phoneDigits } from "./borrower-steps";

/** The borrower has stated that the application has a co-borrower. */
export const COBORROWER_OPT_IN_KEY = "coBorrowerOptIn";
/** The only affirmative value {@link COBORROWER_OPT_IN_KEY} ever holds. */
export const COBORROWER_OPT_IN_VALUE = "yes";

export const COBORROWER_FIRST_NAME_KEY = "coBorrowerFirstName";
export const COBORROWER_LAST_NAME_KEY = "coBorrowerLastName";
export const COBORROWER_PHONE_KEY = "coBorrowerPhone";
export const COBORROWER_EMAIL_KEY = "coBorrowerEmail";
export const COBORROWER_STREET_KEY = "coBorrowerStreetAddress";
export const COBORROWER_CITY_KEY = "coBorrowerCity";
export const COBORROWER_STATE_KEY = "coBorrowerState";
export const COBORROWER_ZIP_KEY = "coBorrowerZipCode";

/** The co-borrower's own answers, without the opt-in that carries them. */
export const COBORROWER_ANSWER_KEYS = [
  COBORROWER_FIRST_NAME_KEY,
  COBORROWER_LAST_NAME_KEY,
  COBORROWER_PHONE_KEY,
  COBORROWER_EMAIL_KEY,
  COBORROWER_STREET_KEY,
  COBORROWER_CITY_KEY,
  COBORROWER_STATE_KEY,
  COBORROWER_ZIP_KEY,
] as const;

/** Every key the co-borrower model owns, opt-in included. */
export const COBORROWER_VALUE_KEYS = [COBORROWER_OPT_IN_KEY, ...COBORROWER_ANSWER_KEYS] as const;

/** A mailing ZIP is exactly five digits, like every other ZIP the SPA accepts. */
const COBORROWER_ZIP_REGEX = /^\d{5}$/;

/** Free-text answers kept as trimmed nonblank text. */
const TEXT_KEYS: readonly string[] = [
  COBORROWER_FIRST_NAME_KEY,
  COBORROWER_LAST_NAME_KEY,
  COBORROWER_STREET_KEY,
  COBORROWER_CITY_KEY,
];

/**
 * Co-borrower value key -> the mapped name the published nested `coBorrower`
 * prefill object arrives under. `coBorrower.creditScoreRange` is deliberately
 * absent: the model has no co-borrower credit answer, so it is ignored.
 */
const MESSAGE_FIELD_NAMES: Readonly<Record<string, string>> = {
  [COBORROWER_FIRST_NAME_KEY]: "coBorrower.firstName",
  [COBORROWER_LAST_NAME_KEY]: "coBorrower.lastName",
  [COBORROWER_PHONE_KEY]: "coBorrower.phone",
  [COBORROWER_EMAIL_KEY]: "coBorrower.email",
  [COBORROWER_STREET_KEY]: "coBorrower.streetAddress",
  [COBORROWER_CITY_KEY]: "coBorrower.city",
  [COBORROWER_STATE_KEY]: "coBorrower.state",
  [COBORROWER_ZIP_KEY]: "coBorrower.zipCode",
};

/**
 * Reads the raw, still-unsanitized co-borrower values a mapped prefill payload
 * carries. An absent or empty nested object simply contributes nothing, which is
 * what makes a message that omits the co-borrower non-destructive: only the
 * explicit clear removes values the borrower already has.
 */
export function readCoBorrowerRawFromMapped(
  mapped: Readonly<Record<string, string>>,
): Record<string, string> {
  const raw: Record<string, string> = {};
  for (const key of COBORROWER_ANSWER_KEYS) {
    const value = mapped[MESSAGE_FIELD_NAMES[key]!];
    if (value !== undefined) raw[key] = value;
  }
  return raw;
}

/**
 * Keeps only the co-borrower values the borrower could themselves have entered,
 * using the same authoritative rules the primary answers use: names and mailing
 * street/city are trimmed nonblank text, the phone is the existing ten-digit
 * canonicalization, the email the existing structural rule, the state an
 * authoritative `STATE_CODES` entry, and the ZIP exactly five digits. Each
 * optional mailing field is judged on its own, so a valid city survives an
 * invalid ZIP beside it.
 *
 * Accepting any answer opts the application in, and an opt-in already carried by
 * the input (a retained snapshot crossing a trusted purpose route) is preserved.
 * An input whose co-borrower values are all invalid opts nothing in and seeds
 * nothing.
 */
export function sanitizeCoBorrowerValues(
  raw: Readonly<Record<string, string | undefined>>,
): Record<string, string> {
  const seeded: Record<string, string> = {};
  const text = (key: string): string => (raw[key] ?? "").trim();

  for (const key of TEXT_KEYS) {
    const value = text(key);
    if (value !== "") seeded[key] = value;
  }

  const phone = text(COBORROWER_PHONE_KEY);
  if (isValidPhoneInput(phone)) seeded[COBORROWER_PHONE_KEY] = phoneDigits(phone);

  const email = text(COBORROWER_EMAIL_KEY);
  if (isValidEmailInput(email)) seeded[COBORROWER_EMAIL_KEY] = email;

  // State codes are compared case-insensitively but stored in the exact casing
  // the authoritative options use, exactly as the property state is.
  const state = text(COBORROWER_STATE_KEY).toUpperCase();
  if (isValidStateCode(state)) seeded[COBORROWER_STATE_KEY] = state;

  const zip = text(COBORROWER_ZIP_KEY);
  if (COBORROWER_ZIP_REGEX.test(zip)) seeded[COBORROWER_ZIP_KEY] = zip;

  const optedIn = text(COBORROWER_OPT_IN_KEY) === COBORROWER_OPT_IN_VALUE;
  if (optedIn || Object.keys(seeded).length > 0) {
    seeded[COBORROWER_OPT_IN_KEY] = COBORROWER_OPT_IN_VALUE;
  }

  return seeded;
}

/** True when the values carry the co-borrower opt-in. */
export function isCoBorrowerOptedIn(values: Readonly<Record<string, string>>): boolean {
  return (values[COBORROWER_OPT_IN_KEY] ?? "") === COBORROWER_OPT_IN_VALUE;
}

/** True when the values carry any co-borrower answer or the opt-in. */
export function hasCoBorrowerValues(values: Readonly<Record<string, string>>): boolean {
  return COBORROWER_VALUE_KEYS.some((key) => (values[key] ?? "") !== "");
}

/** The co-borrower values a snapshot holds, in the model's own keys. */
export function readCoBorrowerValues(
  values: Readonly<Record<string, string>>,
): Record<string, string> {
  const held: Record<string, string> = {};
  for (const key of COBORROWER_VALUE_KEYS) {
    const value = (values[key] ?? "").trim();
    if (value !== "") held[key] = value;
  }
  return held;
}

/**
 * The single co-borrower clear, returning a copy of `values` with every
 * co-borrower answer and the opt-in removed.
 *
 * This is what No, Skip, Remove, and a destructive loan-purpose change all use:
 * one operation, never a per-field removal that could leave the opt-in or a
 * stale answer behind. It is idempotent — clearing twice, or clearing values
 * that never had a co-borrower, yields the same co-borrower-free result — and it
 * touches no primary answer.
 */
export function clearCoBorrowerValues(
  values: Readonly<Record<string, string>>,
): Record<string, string> {
  const cleared: Record<string, string> = { ...values };
  for (const key of COBORROWER_VALUE_KEYS) delete cleared[key];
  return cleared;
}

/**
 * The same clear expressed for a value store that can only set keys (the
 * wizard's flat model): every co-borrower key mapped to the empty value.
 */
export function coBorrowerClearPatch(): Record<string, string> {
  const patch: Record<string, string> = {};
  for (const key of COBORROWER_VALUE_KEYS) patch[key] = "";
  return patch;
}
